import { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Quiz } from 'redux/quiz/types'
import { LegacyQuizScreen } from '../../../config/types'

export interface Navigator {
  previous: () => void
  next: (updatedQuiz?: Quiz) => void
  currentPage: string
  currentPageIndex: number
  hideProgressBar: boolean
}

/* Scroll reset deliberately does not live here. Content scrolls inside
   ScreenShell's region rather than the window, and ScreenShell resets that
   region in a location-keyed useLayoutEffect — which also covers navigations
   that don't go through next/previous (deep links, browser back/forward). */
export const useNavigator = (screens: LegacyQuizScreen[], quiz: Quiz): Navigator => {
  const history = useHistory()
  const location = useLocation<{ step?: number } | undefined>()

  const currentPage = useMemo(() => location.pathname, [location])
  const currentPageIndex = screens.findIndex((screen) => screen.path === location.pathname)
  const hideProgressBar = useMemo(() => shouldHideProgressBar(screens[currentPageIndex]), [currentPageIndex])

  const previous = () => {
    // Prefer native goBack so the browser history stays clean — otherwise
    // pushing a new entry leaves duplicates that flip browser back/forward
    // direction. history.action === 'PUSH' means the current entry came from
    // an in-app navigation and is safe to pop; for deep-links / refreshes
    // (action === 'POP') fall back to push to avoid leaving the site.
    // Multi-step screens push an entry per step, so the entry behind a step is
    // always that screen's previous step, even after a refresh.
    if (history.action === 'PUSH' || (location.state?.step ?? 0) > 0) {
      history.goBack()
    } else {
      const previousScreen: LegacyQuizScreen = getPreviousScreen(quiz, screens, currentPageIndex)
      history.push(previousScreen.path)
    }
  }

  const next = (updatedQuiz?: Quiz) => {
    const latestQuiz = updatedQuiz || quiz
    const nextScreen = getNextScreen(latestQuiz, screens, currentPageIndex)
    history.push(nextScreen.path)
  }

  return { previous, next, currentPage, currentPageIndex, hideProgressBar }
}

export const getNextScreen = (quiz: Quiz, screens: LegacyQuizScreen[], currentPageIndex: number): LegacyQuizScreen => {
  const remainingScreens = screens.slice(currentPageIndex + 1)
  return remainingScreens.find((screen) => shouldShowScreen(screen, quiz)) || screens[currentPageIndex]
}
export const getPreviousScreen = (
  quiz: Quiz,
  screens: LegacyQuizScreen[],
  currentPageIndex: number,
): LegacyQuizScreen => {
  const previousScreens = screens.slice(0, currentPageIndex).reverse()
  return previousScreens.find((screen) => shouldShowScreen(screen, quiz)) || screens[currentPageIndex]
}

const shouldShowScreen = (screen: LegacyQuizScreen, quiz: Quiz) => screen.showIf === undefined || screen.showIf(quiz)

const shouldHideProgressBar = (screen: LegacyQuizScreen) => screen?.hideProgressBar ?? false
